import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CoverThumb } from "@/components/CoverThumb";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PlanningDialog } from "@/components/PlanningDialog";
import { CAT_META, fmtStart, fmtEnd } from "@/lib/planning";
import * as Icons from "lucide-react";

const PREVIEW = 5;
const COUNTERS = ["a_planifier", "planifie", "en_cours"];
const ACTIVE = ["en_cours", "planifie", "a_planifier"];

function CatPill({ cat }) {
  const m = CAT_META[cat] || CAT_META.termine;
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${m.pill}`} data-testid={`pilot-cat-${cat}`}>
    <span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.short}</span>;
}

function Counter({ cat, value, active, onClick }) {
  const m = CAT_META[cat];
  const I = Icons[m.icon];
  return (
    <button onClick={onClick} data-testid={`pilot-counter-${cat}`}
      className={`group flex items-center gap-3 rounded-xl border bg-white p-3 text-left transition-[box-shadow,border-color] hover:shadow-md sm:p-4 ${active ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`}>
      <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${m.tile}`}><I className="h-5 w-5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-mono-data text-2xl font-bold leading-tight text-slate-900" data-testid={`pilot-count-${cat}`}>
          {value === null ? <span className="inline-block h-6 w-6 animate-pulse rounded bg-slate-200 align-middle" /> : value}</span>
        <span className="block truncate text-sm text-slate-500">{m.label}</span>
      </span>
      <Icons.ChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function RowActions({ w, canManage, onPlan, navigate }) {
  const open = () => navigate(`/track/${w.id}`);
  if (w.category === "a_planifier" && canManage)
    return <Button size="sm" variant="outline" onClick={() => onPlan(w)} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`pilot-plan-${w.reference}`}>Planifier</Button>;
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" onClick={open} className="px-2 text-emerald-700 hover:bg-emerald-50" data-testid={`pilot-open-${w.reference}`}>
        {w.category === "en_cours" ? "Voir le suivi" : "Voir le chantier"}<Icons.ArrowRight className="ml-1 h-4 w-4" /></Button>
      {w.category === "planifie" && canManage && (
        <Button size="icon" variant="ghost" onClick={() => onPlan(w)} title="Replanifier" className="h-8 w-8 text-slate-500" data-testid={`pilot-replan-${w.reference}`}><Icons.CalendarClock className="h-4 w-4" /></Button>)}
    </div>
  );
}

function DateCell({ w }) {
  const start = fmtStart(w);
  const end = fmtEnd(w);
  return (
    <div className="text-sm">
      <p className={start ? "text-slate-700" : "text-slate-400"}>{start || "Date à définir"}</p>
      {end && <p className="text-xs text-slate-400">Fin prévue : {end}</p>}
      {w.overdue && <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-rose-600" data-testid={`pilot-overdue-${w.reference}`}><Icons.TriangleAlert className="h-3.5 w-3.5" />Date dépassée — à vérifier</p>}
    </div>
  );
}

function Row({ w, ...p }) {
  const where = [w.site_name, w.commune].filter(Boolean).join(" • ");
  const head = (
    <div className="flex min-w-0 items-center gap-3">
      <CoverThumb path={w.photo_path} alt={w.title} tile={(CAT_META[w.category] || CAT_META.termine).tile} testId={`pilot-${w.reference}`} />
      <div className="min-w-0">
        <p className="truncate font-semibold text-slate-800">{w.title}</p>
        <p className="truncate text-sm text-slate-500">{w.client_name || "—"}{where ? ` · ${where}` : ""}</p>
        {w.responsible_name && <p className="truncate text-xs text-slate-400"><Icons.UserRound className="mr-1 inline h-3 w-3" />{w.responsible_name}</p>}
      </div>
    </div>
  );
  return (
    <li data-testid={`pilot-row-${w.reference}`}>
      <div className="hidden grid-cols-[minmax(0,2.2fr)_minmax(0,0.9fr)_minmax(0,1.3fr)_auto] items-center gap-4 px-4 py-3 lg:grid">
        {head}<div><CatPill cat={w.category} /></div><DateCell w={w} /><div className="justify-self-end"><RowActions w={w} {...p} /></div>
      </div>
      <div className="space-y-2.5 p-4 lg:hidden">
        <div className="flex items-start justify-between gap-2">{head}<CatPill cat={w.category} /></div>
        <div className="flex flex-wrap items-end justify-between gap-2 pl-[76px]"><DateCell w={w} /><RowActions w={w} {...p} /></div>
      </div>
    </li>
  );
}

export function PilotageBoard() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("track.manage");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("tous");
  const [planning, setPlanning] = useState(null);
  const listRef = useRef(null);

  const load = useCallback(() => {
    setError(false);
    api.get("/track/pilotage").then((r) => setData(r.data)).catch(() => { setData(null); setError(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const counts = data?.counts || {};
  const filtered = filter === "tous" ? items.filter((w) => ACTIVE.includes(w.category)) : items.filter((w) => w.category === filter);
  const shown = filtered.slice(0, PREVIEW);
  const pick = (k) => { setFilter(k); listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const tabs = [["tous", "Tous les actifs", ACTIVE.reduce((a, k) => a + (counts[k] || 0), 0)],
    ...ACTIVE.slice().reverse().map((k) => [k, CAT_META[k].label, counts[k] || 0]),
    ...((counts.en_pause || 0) > 0 ? [["en_pause", "En pause", counts.en_pause]] : [])];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="pilot-counters">
        {COUNTERS.map((k) => <Counter key={k} cat={k} value={error ? "—" : data ? counts[k] || 0 : null} active={filter === k} onClick={() => pick(k)} />)}
      </div>

      <div ref={listRef} className="scroll-mt-20">
      <Card className="overflow-hidden" data-testid="pilot-list-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-[#0D2E24]"><Icons.HardHat className="h-5 w-5 text-emerald-600" />Mes chantiers</h2>
          <Button variant="outline" size="sm" onClick={() => navigate("/planning")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="pilot-open-planning">
            <Icons.CalendarDays className="mr-1.5 h-4 w-4" />Ouvrir le planning</Button>
        </div>
        <div className="flex gap-1.5 overflow-x-auto px-4 py-3 sm:px-5" data-testid="pilot-tabs">
          {tabs.map(([k, label, n]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`pilot-tab-${k}`} disabled={!data}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-emerald-100 text-emerald-800" : "text-slate-600 hover:bg-slate-100"}`}>
              {label}{data ? ` (${n})` : ""}</button>))}
        </div>
        <div className="hidden grid-cols-[minmax(0,2.2fr)_minmax(0,0.9fr)_minmax(0,1.3fr)_auto] gap-4 border-y border-slate-100 bg-slate-50/70 px-4 py-2 text-xs font-medium text-slate-500 lg:grid">
          <span>Chantier / Site</span><span>Statut</span><span>Date prévue</span><span className="pr-2 text-right">Action</span>
        </div>
        {error ? (
          <div className="flex flex-col items-center gap-3 p-8 text-center" data-testid="pilot-error">
            <p className="text-sm text-rose-600">Impossible de charger vos chantiers.</p>
            <Button variant="outline" size="sm" onClick={load} data-testid="pilot-retry"><Icons.RotateCw className="mr-1.5 h-4 w-4" />Réessayer</Button>
          </div>
        ) : !data ? (
          <div className="space-y-2 p-4" data-testid="pilot-loading">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : shown.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-8 text-center" data-testid="pilot-empty">
            <Icons.HardHat className="h-9 w-9 text-emerald-500/60" />
            <p className="mt-2 font-semibold text-slate-700">Aucun chantier dans cette catégorie</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">{shown.map((w) => <Row key={w.id} w={w} canManage={canManage} onPlan={setPlanning} navigate={navigate} />)}</ul>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-sm sm:px-5">
          <span className="text-slate-500" data-testid="pilot-shown-count">{data ? `${shown.length} chantier${shown.length > 1 ? "s" : ""} affiché${shown.length > 1 ? "s" : ""} sur ${filtered.length}` : ""}</span>
          <button onClick={() => navigate("/track")} className="inline-flex items-center font-medium text-emerald-700 hover:underline" data-testid="pilot-see-all">Voir tous les chantiers<Icons.ArrowRight className="ml-1 h-4 w-4" /></button>
        </div>
      </Card>
      </div>
      <PlanningDialog open={!!planning} onOpenChange={(o) => !o && setPlanning(null)} ws={planning} onSaved={load} />
    </div>
  );
}
